/**
 * InventoryScanDialog — OCR a printed or handwritten OS&E / inventory page
 * into structured rows on the current list.
 *
 * Flow:
 *   1. User picks a file (camera capture on mobile via `capture="environment"`
 *      attribute, or normal file picker on desktop).
 *   2. File is uploaded to /api/admin/files/upload → returns a server URL.
 *   3. POST /api/admin/workspace/inventory/ocr-scan with the URL + mode.
 *   4. Server calls GPT-5.2 vision, returns parsed rows, persists them
 *      against the active list.
 *   5. The parent table refreshes (parent passes onComplete).
 *
 * Modes: 'append' (default) adds rows to the existing list, 'replace'
 * wipes the active list first.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Camera, Loader2, Upload, FileImage } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryScanDialog({ open, onClose, nodeId, listId = 'default', listLabel = '', onComplete }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [mode, setMode] = useState('append');
  const [stage, setStage] = useState('idle'); // 'idle' | 'uploading' | 'scanning' | 'done'

  const reset = () => {
    setFile(null);
    if (previewUrl) try { URL.revokeObjectURL(previewUrl); } catch (e) { /* ignore */ }
    setPreviewUrl('');
    setMode('append');
    setStage('idle');
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    if (f.type.startsWith('image/')) {
      try { setPreviewUrl(URL.createObjectURL(f)); } catch (err) { setPreviewUrl(''); }
    } else {
      setPreviewUrl('');
    }
  };

  const runScan = async () => {
    if (!file || !adminToken) return;
    setStage('uploading');
    try {
      // Step 1: upload the file to get a server-side URL.
      const fd = new FormData();
      fd.append('file', file);
      const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const fileUrl = up?.url || up?.file_url || up?.path;
      if (!fileUrl) throw new Error('Upload did not return a URL');

      // Step 2: trigger OCR scan.
      setStage('scanning');
      const { data } = await axios.post(
        `${API}/admin/workspace/inventory/ocr-scan`,
        { node_id: nodeId, list_id: listId, file_url: fileUrl, mode },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setStage('done');
      toast.success(`Scan complete · ${data.inserted} row${data.inserted === 1 ? '' : 's'} ${mode === 'replace' ? 'imported (list replaced)' : 'appended'}.`);
      onComplete?.();
      handleClose();
    } catch (e) {
      setStage('idle');
      toast.error(e?.response?.data?.detail || e?.message || 'Scan failed');
    }
  };

  const handleClose = () => {
    if (stage === 'uploading' || stage === 'scanning') return; // block close during async work
    reset();
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg" data-testid="inventory-scan-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <Camera className="w-5 h-5 text-blue-600" />
            Scan inventory sheet
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500" style={{ fontFamily: 'Manrope' }}>
            Scan a printed page <strong>or</strong> a handwritten OS&amp;E list — AI vision extracts each row into the form. Importing into <strong>{listLabel || 'this list'}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File picker (camera on mobile, file picker on desktop) */}
          <label
            data-testid="inventory-scan-pick"
            className="block border-2 border-dashed rounded-xl px-4 py-8 text-center cursor-pointer transition-colors hover:bg-blue-50"
            style={{ borderColor: '#C9D6E6', fontFamily: 'Manrope' }}
          >
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              onChange={onPick}
              disabled={stage !== 'idle'}
              className="hidden"
              data-testid="inventory-scan-file-input"
            />
            {previewUrl ? (
              <img src={previewUrl} alt="Scan preview" className="max-h-48 mx-auto rounded shadow-sm" />
            ) : file ? (
              <div className="flex flex-col items-center gap-1.5 text-blue-700">
                <FileImage className="w-8 h-8" />
                <span className="text-sm font-bold">{file.name}</span>
                <span className="text-[10px] text-gray-500">{(file.size / 1024).toFixed(0)} KB</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 text-blue-700">
                <Camera className="w-8 h-8" />
                <span className="text-sm font-bold">Tap to scan with camera</span>
                <span className="text-[10px] text-gray-500">or pick an image / PDF (max 10 MB)</span>
              </div>
            )}
          </label>

          {/* Mode picker */}
          <fieldset className="space-y-1.5">
            <legend className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>
              Import mode
            </legend>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio" name="ocr-mode" value="append"
                checked={mode === 'append'}
                onChange={() => setMode('append')}
                data-testid="inventory-scan-mode-append"
                className="mt-1"
                style={{ accentColor: '#003DA5' }}
              />
              <div>
                <p className="text-xs font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>Append (recommended)</p>
                <p className="text-[10px] text-gray-500">Add the scanned rows below the existing items in this list.</p>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio" name="ocr-mode" value="replace"
                checked={mode === 'replace'}
                onChange={() => setMode('replace')}
                data-testid="inventory-scan-mode-replace"
                className="mt-1"
                style={{ accentColor: '#DC2626' }}
              />
              <div>
                <p className="text-xs font-bold text-red-700" style={{ fontFamily: 'Manrope' }}>Replace</p>
                <p className="text-[10px] text-gray-500">Delete every existing row in this list, then import the scan.</p>
              </div>
            </label>
          </fieldset>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={stage === 'uploading' || stage === 'scanning'}
            data-testid="inventory-scan-cancel"
            className="px-4 py-2 rounded-lg text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-40"
            style={{ fontFamily: 'Manrope' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={runScan}
            disabled={!file || stage !== 'idle'}
            data-testid="inventory-scan-run"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)', fontFamily: 'Manrope' }}
          >
            {stage === 'uploading' ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
              : stage === 'scanning' ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading sheet…</>
              : <><Upload className="w-4 h-4" /> Run OCR scan</>
            }
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
